import type { FormatDistanceOptions } from 'date-fns/formatDistance';
import { formatDistance } from 'date-fns/formatDistance';
import type { FormatDistanceStrictOptions } from 'date-fns/formatDistanceStrict';
import { formatDistanceStrict } from 'date-fns/formatDistanceStrict';

export class DateFormatter {
  private readonly locale: string;
  private readonly formatter: Intl.DateTimeFormat;
  private readonly formatterLong: Intl.DateTimeFormat;
  private readonly formatterDateOnly: Intl.DateTimeFormat;
  private readonly formatterCalendarDateOnlyUTC: Intl.DateTimeFormat;
  private readonly formatterTimeOnly: Intl.DateTimeFormat;

  constructor(
    locale: string,
    options?: Pick<
      Intl.DateTimeFormatOptions,
      'timeZone' | 'timeZoneName' | 'formatMatcher'
    >,
  ) {
    this.locale = locale;

    this.formatter = new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      ...options,
    });

    this.formatterLong = new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      ...options,
    });

    this.formatterDateOnly = new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
      day: '2-digit',
      ...options,
    });

    this.formatterCalendarDateOnlyUTC = new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
      day: 'numeric',
      timeZone: 'UTC',
    });

    this.formatterTimeOnly = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      ...options,
    });
  }

  format(value: Date): string {
    return this.formatter.format(value);
  }

  formatLong(value: Date): string {
    return this.formatterLong.format(value);
  }

  formatDateOnly(value: Date): string {
    return this.formatterDateOnly.format(value);
  }

  formatCalendarDateOnlyUTC({
    year,
    month,
    day,
  }: {
    year: number;
    month: number;
    day: number;
  }): string {
    return this.formatterCalendarDateOnlyUTC.format(
      new Date(Date.UTC(year, month - 1, day)),
    );
  }

  formatTimeOnly(value: Date): string {
    return this.formatterTimeOnly.format(value);
  }

  fromString(value: string): string {
    return this.format(new Date(value));
  }

  formatDistance(
    laterDate: Date,
    earlierDate: Date = new Date(),
    options?: FormatDistanceOptions,
  ): string {
    return formatDistance(laterDate, earlierDate, options);
  }

  formatDistanceStrict(
    laterDate: Date,
    earlierDate: Date = new Date(),
    options?: FormatDistanceStrictOptions,
  ): string {
    return formatDistanceStrict(laterDate, earlierDate, options);
  }

  formatCompactRelativeTime(
    date: Date | string,
    now: Date = new Date(),
  ): string {
    const target = typeof date === 'string' ? new Date(date) : date;
    const diffMs = Math.max(0, now.getTime() - target.getTime());
    const minutes = Math.floor(diffMs / 60_000);

    if (minutes < 1) {
      return 'now';
    }
    if (minutes < 60) {
      return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return `${days}d`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months}mo`;
    }

    return `${Math.floor(months / 12)}y`;
  }

  formatDuration(durationInMs: number | undefined): string {
    if (!durationInMs) {
      return '';
    }

    const hours = Math.floor(durationInMs / 3_600_000);
    const minutes = Math.floor((durationInMs % 3_600_000) / 60_000);
    const remainingSeconds = Math.floor((durationInMs % 60_000) / 1_000);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')} hours`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} mins`;
    }
  }

  /**
   * Formats a date string for display in trend chart legends.
   * Handles both YYYY-MM format (from Discovery Trends API) and month names (from Signal Generation API).
   *
   * @param dateString - Either YYYY-MM format (e.g., "2024-01") or month name (e.g., "Jan")
   * @param index - Optional index for calculating year context when dealing with month names
   * @returns Formatted date string (e.g., "Jan 2024")
   */
  formatTrendDate(dateString: string, index?: number): string {
    try {
      // If it's already a month name (like "Jan", "Feb"), add current year context for consistency
      if (this.isMonthName(dateString)) {
        return this.formatMonthNameWithYear(dateString, index);
      }

      // Handle YYYY-MM format (for Discovery Trends compatibility)
      if (this.isYearMonthFormat(dateString)) {
        return this.formatYearMonthString(dateString);
      }

      // Return as-is if format is not recognized
      return dateString;
    } catch (error) {
      console.warn('Failed to parse trend date:', dateString, error);
      return dateString;
    }
  }

  /**
   * Checks if the string is a valid month name.
   */
  private isMonthName(dateString: string): boolean {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return monthNames.includes(dateString);
  }

  /**
   * Adds year context to month names based on current date and index position.
   * Assumes a 6-month rolling window going backwards from current date.
   */
  private formatMonthNameWithYear(monthName: string, index?: number): string {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based

    // Calculate which month this represents (assuming 6 months back from current)
    const targetMonth = currentMonth - (5 - (index || 0)); // 6 months window, index from 0

    let displayYear = currentYear;
    if (targetMonth < 0) {
      displayYear = currentYear - 1;
    }

    return `${monthName} ${displayYear}`;
  }

  /**
   * Formats YYYY-MM string to "Month Year" format.
   */
  private formatYearMonthString(dateString: string): string {
    const [year, month] = dateString.split('-');
    if (year && month) {
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    }
    return dateString;
  }

  /**
   * Checks if the string is in YYYY-MM format.
   */
  private isYearMonthFormat(dateString: string): boolean {
    const yearMonthPattern = /^\d{4}-\d{1,2}$/;
    return yearMonthPattern.test(dateString);
  }

  /**
   * Formats the next scheduled execution time for a given day and time in UTC.
   * Returns a human-readable string like "Monday at 9:00 AM EST".
   *
   * @param dayOfWeek - Target day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   * @param hourUTC - Hour in UTC (0-23)
   * @param minuteUTC - Minute in UTC (0-59)
   * @returns Formatted string with day of week and local time
   */
  futureLocalDayTime(dayOfWeek: number, hourUTC = 0, minuteUTC = 0): string {
    const now = new Date();
    const currentDay = now.getUTCDay();

    // Calculate days until target day
    let daysUntilTarget = dayOfWeek - currentDay;

    // If target day is in the past, schedule for next week
    if (daysUntilTarget < 0) {
      daysUntilTarget += 7;
    } else if (daysUntilTarget === 0) {
      // If today is the target day, check if the scheduled time has passed
      const scheduledTimeToday = new Date(now);
      scheduledTimeToday.setUTCHours(hourUTC, minuteUTC, 0, 0);

      // If scheduled time has already passed today, use next week
      if (now >= scheduledTimeToday) {
        daysUntilTarget = 7;
      }
    }

    const targetDate = new Date(now);
    targetDate.setUTCDate(now.getUTCDate() + daysUntilTarget);
    targetDate.setUTCHours(hourUTC, minuteUTC, 0, 0);

    const formattedDayOfWeek = targetDate.toLocaleDateString('en-US', {
      weekday: 'long',
    });

    const formattedTimeOnDay = targetDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });

    return `${formattedDayOfWeek} at ${formattedTimeOnDay}`;
  }
}

export const dateComparator = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  const dateA = new Date(a);
  const dateB = new Date(b);

  const timeA = dateA.getTime();
  const timeB = dateB.getTime();

  if (isNaN(timeA) && isNaN(timeB)) return 0;
  if (isNaN(timeA)) return -1;
  if (isNaN(timeB)) return 1;

  return timeA - timeB;
};
